<template>
  <div class="execucao-root">

    <!-- Cabeçalho da seção -->
    <div class="execucao-header">
      <div class="execucao-header__info">
        <span class="execucao-header__count" v-if="item.acoes.length > 0">
          {{ item.acoes.length }} {{ item.acoes.length !== 1 ? 'ações' : 'ação' }} configurada{{ item.acoes.length !== 1 ? 's' : '' }}
        </span>
      </div>
      <button type="button" class="btn-add-acao" v-on:click="addAcao">
        <span class="btn-add-acao__icon">+</span>
        Adicionar Ação
      </button>
    </div>

    <!-- Estado vazio -->
    <div class="execucao-empty" v-if="item.acoes.length === 0">
      <div class="execucao-empty__icon">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d="M13 10V3L4 14h7v7l9-11h-7z"/>
        </svg>
      </div>
      <p class="execucao-empty__title">Nenhuma ação configurada</p>
      <p class="execucao-empty__sub">Clique em "Adicionar Ação" para definir o que acontece quando esta regra for acionada.</p>
    </div>

    <!-- Lista de ações -->
    <div class="acao-card" v-for="(acao, index) in item.acoes" :key="index" :class="{ 'acao-card--collapsed': collapsedAcoes[index] }">

      <!-- Header do card -->
      <div class="acao-card__header" v-on:click="toggleCollapse(index)">
        <div class="acao-card__header-left">
          <span class="acao-card__step">{{ index + 1 }}</span>
          <span class="acao-card__title">
            {{ acao.acao.class_method_or_sql ? shortLabel(acao.acao.class_method_or_sql) : 'Nova Ação' }}
          </span>
          <span class="acao-card__tipo-badge" v-if="acao.tipo_execucao" :class="'acao-card__tipo-badge--' + acao.tipo_execucao.toLowerCase()">
            {{ acao.tipo_execucao }}
          </span>
        </div>
        <div class="acao-card__header-actions">
          <button type="button" class="acao-card__collapse" :title="collapsedAcoes[index] ? 'Expandir' : 'Minimizar'">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
              :style="{ transform: collapsedAcoes[index] ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>
          <button type="button" class="acao-card__delete" v-on:click.stop="deleteAcao(acao)" title="Remover ação">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Corpo do card -->
      <div class="acao-card__body" v-show="!collapsedAcoes[index]">

        <!-- Passo 1: Tipo -->
        <div class="acao-step">
          <div class="acao-step__label">
            <span class="acao-step__num">1</span>
            Tipo de Execução
          </div>
          <div class="tipo-toggle">
            <button
              type="button"
              class="tipo-toggle__btn"
              v-for="tipo in ticketAcaoExecucaoTipo"
              :key="tipo"
              :class="{ 'tipo-toggle__btn--active': acao.tipo_execucao === tipo }"
              v-on:click="acao.tipo_execucao = tipo"
            >
              <span class="tipo-toggle__icon" v-if="tipo === 'JOB'">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
              </span>
              <span class="tipo-toggle__icon" v-else>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
              </span>
              {{ tipo }}
            </button>
          </div>
          <p class="acao-step__hint" v-if="acao.tipo_execucao === 'JOB'">Executa uma classe PHP pré-definida com parâmetros configuráveis.</p>
          <p class="acao-step__hint" v-else-if="acao.tipo_execucao === 'SQL'">Executa uma query SQL diretamente no banco de dados.</p>
          <p class="acao-step__hint" v-else-if="acao.tipo_execucao === 'PROCEDURE'">Executa uma stored procedure no banco de dados.</p>
          <p class="acao-step__hint acao-step__hint--muted" v-else>Selecione o tipo para continuar.</p>
        </div>

        <!-- Passo 2: Ação -->
        <div class="acao-step" v-if="acao.tipo_execucao">
          <div class="acao-step__label">
            <span class="acao-step__num">2</span>
            {{ acao.tipo_execucao === 'JOB' ? 'Selecionar Ação' : 'Código SQL' }}
          </div>
          <div class="acao-step__field">
            <ModelListSelect
              v-if="acao.tipo_execucao === 'JOB'"
              :list="acoesLista"
              v-model="acao.acao.class_method_or_sql"
              option-value="class_method_or_sql"
              option-text="titulo"
              placeholder="Pesquisar ação..."
              @input="autoFillPhpClassParams(acao)">
            </ModelListSelect>
            <codemirror
              v-if="acao.tipo_execucao !== 'JOB'"
              v-model="acao.acao.class_method_or_sql"
              :options="code_options">
            </codemirror>
          </div>
          <p class="acao-step__hint" v-if="acao.tipo_execucao === 'JOB'">
            Ex: <code>App\Services\Ticket\Actions\EnviarEmailPadrao@run</code>
          </p>
          <p class="acao-step__hint" v-if="acao.tipo_execucao === 'SQL'">
            Ex: <code>UPDATE table SET coluna = 1 WHERE id = :ticket_id</code>
          </p>
        </div>

        <!-- Passo 3: Parâmetros (apenas JOB com ação selecionada) -->
        <div
          class="acao-step"
          v-if="acao.tipo_execucao === 'JOB' && acao.acao.class_method_or_sql"
        >
          <div class="acao-step__label">
            <span class="acao-step__num">3</span>
            Parâmetros
          </div>

          <!-- Tabela de parâmetros simples (não-corpo-de-email) -->
          <div class="params-table" v-if="hasNonEmailBodyParams(acao.acao.class_method_or_sql)">
            <div class="params-table__head">
              <span>Parâmetro</span>
              <span>Tipo</span>
              <span>Padrão</span>
              <span>Valor</span>
              <span></span>
            </div>
            <template v-for="(param, key) in getParamsClassMethod(acao.acao.class_method_or_sql)">
              <div
                v-if="!isEmailBodyParam(param) && !isPhpClassParam(param)"
                class="params-table__row"
                :key="key"
                :class="{ 'params-table__row--required': !param.is_optional }"
              >
                <span class="params-table__name">{{ param.name }}</span>
                <span class="params-table__type">
                  <span v-if="param.class" class="type-pill type-pill--class" :title="param.class">
                    {{ param.class.split('\\').pop() }}
                  </span>
                  <span v-else class="type-pill type-pill--text">string</span>
                </span>
                <span class="params-table__default">
                  <code v-if="param.value">{{ param.value }}</code>
                  <span v-else class="text-muted">—</span>
                </span>
                <span class="params-table__input">
                  <select v-if="isBoolParam(param)" class="form-control form-control-sm" v-model="acao.acao.params[key]">
                    <option :value="1">Sim</option>
                    <option :value="0">Não</option>
                  </select>
                  <input v-else class="form-control form-control-sm" v-model="acao.acao.params[key]" :placeholder="param.value || ''" />
                </span>
                <span class="params-table__badge">
                  <span class="req-badge req-badge--required" v-if="!param.is_optional">Req</span>
                  <span class="req-badge req-badge--optional" v-else>Opt</span>
                </span>
              </div>
            </template>
          </div>

          <!-- Editores ricos para parâmetros de corpo de e-mail -->
          <template v-for="(param, key) in getParamsClassMethod(acao.acao.class_method_or_sql)">
            <div v-if="isEmailBodyParam(param)" :key="'email-' + key" class="email-body-param">
              <div class="email-body-param__header">
                <span class="params-table__name">{{ param.name }}</span>
                <span class="type-pill type-pill--text" style="margin-left:6px">string (HTML)</span>
                <span class="req-badge req-badge--required" v-if="!param.is_optional" style="margin-left:6px">Req</span>
                <span class="req-badge req-badge--optional" v-else style="margin-left:6px">Opt</span>
                <span class="email-body-param__badge">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                  Corpo do e-mail
                </span>
              </div>
              <EmailBodyEditor
                v-model="acao.acao.params[key]"
                :all-params="acao.acao.params"
                :params-meta="getParamsClassMethod(acao.acao.class_method_or_sql)"
              />
            </div>
          </template>

        </div>

      </div>
    </div>

    <!-- Ajuda contextual -->
    <div class="execucao-help">
      <div class="execucao-help__grid">
        <div class="help-item">
          <div class="help-item__icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
          </div>
          <div>
            <strong>JOB</strong>
            <p>Executa uma classe PHP predefinida. Use <code>Classe@método</code>. Os parâmetros do método são configuráveis no passo 3.</p>
            <pre>App\Services\Ticket\Actions\EnviarEmail@run</pre>
          </div>
        </div>
        <div class="help-item">
          <div class="help-item__icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
          </div>
          <div>
            <strong>SQL / PROCEDURE</strong>
            <p>Executa uma query ou stored procedure diretamente no banco.</p>
            <pre>UPDATE tickets SET status = 1 WHERE id = :ticket_id</pre>
          </div>
        </div>
        <div class="help-item">
          <div class="help-item__icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M9 9h.01M12 12v4M12 8h.01"/></svg>
          </div>
          <div>
            <strong>Parâmetros</strong>
            <p>Disponíveis apenas para JOB. Representam os argumentos do método PHP. Campos obrigatórios <span class="help-badge help-badge--req">Req</span> devem ser preenchidos.</p>
            <pre>public function run(Ticket $ticket, string $titulo)</pre>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { ModelListSelect } from 'vue-search-select'
import 'vue-search-select/dist/VueSearchSelect.css'

import { codemirror } from 'vue-codemirror'
import 'codemirror/lib/codemirror.css'
import 'codemirror/theme/monokai.css'
import 'codemirror/addon/selection/active-line.js'
import 'codemirror/mode/sql/sql.js'
import 'codemirror/addon/display/autorefresh.js'
import 'codemirror/addon/edit/closetag.js'

import _filter from 'lodash/filter'

import EmailBodyEditor from './EmailBodyEditor.vue'

// Nomes de parâmetros que representam o corpo de um e-mail
const EMAIL_BODY_PARAM_NAMES = ['mensagem', 'mensagememail', 'body', 'conteudo', 'html']

const novaAcao = (acao_objeto_id) => ({
  id: null,
  acao_objeto_id,
  tipo_execucao: null,
  acao: { class_method_or_sql: null, params: [] },
})

export default {
  name: 'acoes',
  components: { codemirror, ModelListSelect, EmailBodyEditor },
  props: ['item', 'ticketAcaoExecucaoAcao', 'ticketAcaoExecucaoTipo'],
  computed: {
    acoesLista() {
      return (this.ticketAcaoExecucaoAcao || []).map((a) => ({
        ...a,
        titulo: a.label || a.class_method_or_sql,
      }))
    },
  },
  data() {
    return {
      collapsedAcoes: {},
      code_options: {
        tabSize: 4,
        styleActiveLine: true,
        lineNumbers: true,
        line: true,
        mode: 'text/x-sql',
        autoRefresh: true,
        lineWrapping: true,
        theme: 'monokai',
        showCursorWhenSelecting: true,
        highlightSelectionMatches: { showToken: /\w/, annotateScrollbar: true },
      },
    }
  },
  methods: {
    addAcao() {
      this.item.acoes.push(novaAcao(this.item.id))
    },
    deleteAcao(acao) {
      this.item.acoes = _filter(this.item.acoes, (p) => p !== acao)
    },
    toggleCollapse(index) {
      this.$set(this.collapsedAcoes, index, !this.collapsedAcoes[index])
    },
    getParamsClassMethod(classMethod) {
      if (!classMethod) return undefined
      const match = _filter(this.ticketAcaoExecucaoAcao, (p) => p.class_method_or_sql === classMethod)[0]
      return match ? match.params : undefined
    },
    shortLabel(val) {
      if (!val) return 'Nova Ação'
      const match = _filter(this.acoesLista, (p) => p.class_method_or_sql === val)[0]
      if (match && match.titulo && match.titulo !== val) return match.titulo
      const parts = val.split('\\')
      return parts[parts.length - 1] || val
    },
    /** Detecta se o parâmetro é uma classe PHP (ex: App\Models\Ticket) — resolvido automaticamente */
    isPhpClassParam(param) {
      return param && param.class && param.class.includes('\\')
    },
    /** Detecta se o parâmetro é do tipo booleano */
    isBoolParam(param) {
      return param && param.class === 'bool'
    },
    /** Detecta se o parâmetro representa o corpo de um e-mail */
    isEmailBodyParam(param) {
      if (!param || this.isPhpClassParam(param)) return false
      const nameLower = (param.name || '').toLowerCase().replace(/[^a-z]/g, '')
      return EMAIL_BODY_PARAM_NAMES.some((n) => nameLower === n)
    },
    /** Verifica se a ação tem ao menos um parâmetro configurável pelo usuário */
    hasNonEmailBodyParams(classMethod) {
      const params = this.getParamsClassMethod(classMethod)
      if (!params) return false
      return Object.values(params).some((p) => !this.isEmailBodyParam(p) && !this.isPhpClassParam(p))
    },
    /** Auto-preenche parâmetros de classe PHP com :nomemodelo */
    autoFillPhpClassParams(acao) {
      const params = this.getParamsClassMethod(acao.acao.class_method_or_sql)
      if (!params) return
      Object.entries(params).forEach(([key, param]) => {
        if (this.isPhpClassParam(param)) {
          const modelName = param.class.split('\\').pop().toLowerCase()
          this.$set(acao.acao.params, key, `:${modelName}`)
        }
      })
    },
  },
}
</script>

<style scoped>
/* ── Root ───────────────────────────────────────────── */
.execucao-root {
  display: flex;
  flex-direction: column;
  gap: 0;
}

/* ── Cabeçalho da seção ─────────────────────────────── */
.execucao-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.execucao-header__info {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
}

.btn-add-acao {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #1d4ed8;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 7px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, transform 0.1s;
  letter-spacing: 0.01em;
}

.btn-add-acao:hover {
  background: #1e40af;
}

.btn-add-acao:active {
  transform: scale(0.98);
}

.btn-add-acao__icon {
  font-size: 18px;
  line-height: 1;
  font-weight: 300;
}

/* ── Estado vazio ───────────────────────────────────── */
.execucao-empty {
  text-align: center;
  padding: 40px 24px;
  border: 2px dashed #e5e7eb;
  border-radius: 8px;
  margin-bottom: 16px;
  color: #9ca3af;
}

.execucao-empty__icon {
  margin: 0 auto 12px;
  width: 48px;
  height: 48px;
  background: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #d1d5db;
}

.execucao-empty__title {
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
  margin: 0 0 4px;
}

.execucao-empty__sub {
  font-size: 12px;
  color: #9ca3af;
  margin: 0;
  max-width: 320px;
  margin: 0 auto;
  line-height: 1.5;
}

/* ── Card da ação ───────────────────────────────────── */
.acao-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 12px;
  background: #fff;
  transition: box-shadow 0.15s;
}

.acao-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.acao-card--collapsed .acao-card__header {
  border-bottom: none;
  border-radius: 8px;
}

.acao-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  border-radius: 8px 8px 0 0;
  cursor: pointer;
  user-select: none;
}

.acao-card__header:hover {
  background: #f3f4f6;
}

.acao-card__header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.acao-card__collapse {
  background: transparent;
  color: #9ca3af;
  border: 1px solid transparent;
  border-radius: 4px;
  width: 28px;
  height: 28px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  flex-shrink: 0;
  transition: background 0.15s, color 0.15s;
}

.acao-card__collapse:hover {
  background: #e5e7eb;
  color: #374151;
}

.acao-card__header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.acao-card__step {
  width: 22px;
  height: 22px;
  background: #1d4ed8;
  color: #fff;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.acao-card__title {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 300px;
}

.acao-card__tipo-badge {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 3px;
  letter-spacing: 0.06em;
  flex-shrink: 0;
}

.acao-card__tipo-badge--job {
  background: #dbeafe;
  color: #1d4ed8;
}

.acao-card__tipo-badge--sql {
  background: #fef3c7;
  color: #92400e;
}

.acao-card__delete {
  background: transparent;
  color: #9ca3af;
  border: 1px solid transparent;
  border-radius: 4px;
  width: 28px;
  height: 28px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  flex-shrink: 0;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}

.acao-card__delete:hover {
  background: #fee2e2;
  color: #dc2626;
  border-color: #fecaca;
}

.acao-card__body {
  padding: 0;
}

/* ── Passos dentro do card ──────────────────────────── */
.acao-step {
  padding: 14px 16px;
  border-bottom: 1px solid #f3f4f6;
}

.acao-step:last-child {
  border-bottom: none;
}

.acao-step__label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 10px;
}

.acao-step__num {
  width: 18px;
  height: 18px;
  background: #e5e7eb;
  color: #374151;
  border-radius: 50%;
  font-size: 10px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.acao-step__field {
  /* deixa o dropdown expandir */
}

.acao-step__hint {
  margin: 8px 0 0;
  font-size: 11px;
  color: #6b7280;
  line-height: 1.5;
}

.acao-step__hint--muted {
  color: #9ca3af;
  font-style: italic;
}

.acao-step__hint code {
  background: #f3f4f6;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 11px;
  color: #374151;
}

/* ── Toggle tipo execução ───────────────────────────── */
.tipo-toggle {
  display: inline-flex;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  overflow: hidden;
  background: #f9fafb;
}

.tipo-toggle__btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 7px 18px;
  background: transparent;
  border: none;
  border-right: 1px solid #d1d5db;
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;
}

.tipo-toggle__btn:last-child {
  border-right: none;
}

.tipo-toggle__btn:hover:not(.tipo-toggle__btn--active) {
  background: #f3f4f6;
  color: #374151;
}

.tipo-toggle__btn--active {
  background: #1d4ed8;
  color: #fff;
  font-weight: 600;
}

.tipo-toggle__icon {
  display: flex;
  align-items: center;
}

/* ── Tabela de parâmetros ───────────────────────────── */
.params-table {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  overflow: hidden;
  font-size: 12px;
}

.params-table__head {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) 80px 100px minmax(0, 1.6fr) 44px;
  gap: 0;
  background: #f3f4f6;
  border-bottom: 1px solid #e5e7eb;
  padding: 6px 12px;
  font-size: 10px;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.params-table__row {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) 80px 100px minmax(0, 1.6fr) 44px;
  align-items: center;
  gap: 0;
  padding: 8px 12px;
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.1s;
}

.params-table__row:last-child {
  border-bottom: none;
}

.params-table__row:hover {
  background: #fafafa;
}

.params-table__row--required {
  border-left: 3px solid #ef4444;
}

.params-table__row--required .params-table__name {
  padding-left: 0;
}

.params-table__name {
  font-weight: 600;
  color: #1d4ed8;
  font-family: monospace;
  font-size: 12px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.type-pill {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 80px;
}

.type-pill--class {
  background: #dbeafe;
  color: #1e40af;
}

.type-pill--text {
  background: #f3f4f6;
  color: #6b7280;
}

.params-table__default code {
  font-size: 11px;
  background: #f3f4f6;
  padding: 1px 5px;
  border-radius: 3px;
  color: #374151;
}

.params-table__default .text-muted {
  color: #d1d5db;
}

.params-table__input .form-control {
  height: 30px;
  font-size: 12px;
  padding: 4px 8px;
  border-color: #d1d5db;
  border-radius: 4px;
}

.params-table__input .form-control:focus {
  border-color: #1d4ed8;
  box-shadow: 0 0 0 2px rgba(29, 78, 216, 0.12);
}

.params-table__badge {
  display: flex;
  justify-content: center;
}

.req-badge {
  font-size: 9px;
  font-weight: 700;
  padding: 2px 5px;
  border-radius: 3px;
  letter-spacing: 0.04em;
}

.req-badge--required {
  background: #fee2e2;
  color: #dc2626;
}

.req-badge--optional {
  background: #f3f4f6;
  color: #9ca3af;
}

/* ── Ajuda contextual ───────────────────────────────── */
.execucao-help {
  margin-top: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
  padding: 14px 16px;
}

.execucao-help__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.help-item {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.help-item__icon {
  width: 28px;
  height: 28px;
  background: #e5e7eb;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  flex-shrink: 0;
  margin-top: 1px;
}

.help-item strong {
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: #374151;
  margin-bottom: 3px;
}

.help-item p {
  font-size: 11px;
  color: #6b7280;
  line-height: 1.5;
  margin: 0;
}

.help-item code {
  background: #e5e7eb;
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 10px;
  color: #374151;
}

.help-item pre {
  background: #1e1e1e;
  color: #d4d4d4;
  font-size: 10px;
  padding: 6px 8px;
  border-radius: 4px;
  margin: 6px 0 0;
  overflow-x: auto;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-all;
}

.help-badge {
  display: inline-block;
  font-size: 9px;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 3px;
  vertical-align: middle;
}

.help-badge--req {
  background: #fee2e2;
  color: #dc2626;
}

/* ── Parâmetro de corpo de e-mail ───────────────────── */
.email-body-param {
  margin-top: 10px;
}

.email-body-param__header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}

.email-body-param__badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  padding: 2px 8px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  color: #166534;
}
</style>

<style>
/* CodeMirror dentro do card */
.acao-card .CodeMirror {
  font-size: 12px;
  height: 130px;
  border-radius: 4px;
  border: 1px solid #d1d5db;
}

/* Dropdown ModelListSelect: sem corte */
.acao-step__field .ui.fluid.search.selection.dropdown .menu {
  min-width: 100%;
  width: max-content;
  max-width: 640px;
}

.acao-step__field .ui.fluid.search.selection.dropdown .menu .item {
  white-space: normal;
  word-break: break-all;
  font-size: 12px;
  line-height: 1.4;
  padding: 8px 10px !important;
}

.acao-step__field .ui.fluid.search.selection.dropdown > .text,
.acao-step__field .ui.fluid.search.selection.dropdown input {
  font-size: 13px;
}
</style>
